// Dati di Projects V2: elenco, dettaglio, creazione, modifica, eliminazione, activities e revenues del progetto.
import { API_ENDPOINTS } from '../config/api.config';
import { ENABLE_TAB_MAPPING } from '../config/local.config';
import { parseLocalDate } from './dateHelpers';

async function request(url, options) {
  const res = await fetch(url, options);
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok) throw new Error(body?.error || body?.message || `Request failed (${res.status})`);
  return body || {};
}

const jsonOptions = (method, payload) => ({
  method,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  body: JSON.stringify(payload),
});

// ---- Elenco (con cache breve: lista e dettaglio lo usano entrambi, anche per il selettore dei progetti) ----
const LIST_TTL = 60 * 1000;
let listCache = null;

/**
 * Progetti visibili, stessa regola della pagina classica:
 * - Admin, Delivery Manager e altri ruoli: tutti i progetti
 * - Service Manager / JR SM / Team Leader: progetti con ruolo 1,2,3 ancora validi
 * I progetti arrivano sempre completi (anche per il Service Manager), senza doppioni.
 */
export function fetchProjectsList({ ownOnly, userId }) {
  const key = ownOnly ? `own:${userId}` : 'all';
  if (listCache && listCache.key === key && Date.now() - listCache.at < LIST_TTL) return listCache.promise;
  const promise = (async () => {
    const [all, own] = await Promise.all([
      request(API_ENDPOINTS.PROJECTS),
      ownOnly ? request(`${API_ENDPOINTS.PROJECT_USERS}?user_id=${userId}`) : null,
    ]);
    const projects = all.projects || [];
    if (!own) return projects;
    const now = new Date();
    const ids = new Set(
      (own.user_projects_with_references || [])
        .filter((item) => [1, 2, 3].includes(item.project_role?.id) && (!item.validity_end_date || parseLocalDate(item.validity_end_date) > now))
        .map((item) => String(item.project?.id)),
    );
    return projects.filter((p) => ids.has(String(p.id)));
  })();
  listCache = { key, at: Date.now(), promise };
  promise.catch(() => {
    if (listCache?.promise === promise) listCache = null;
  });
  return promise;
}

export const invalidateProjectsList = () => {
  listCache = null;
};

/** Dati di contorno per filtri e form: clienti e tipi di servizio. */
export async function fetchProjectLookups() {
  const [c, t] = await Promise.all([request(API_ENDPOINTS.CUSTOMERS), request(`${API_ENDPOINTS.REF_LOOKUP}?field_key=contract_type`).catch(() => ({ items: [] }))]);
  return { customers: c.customers || [], serviceTypes: t.items || [] };
}

export async function fetchProject(id) {
  const data = await request(`${API_ENDPOINTS.PROJECTS}?id=${id}`);
  return data.project || data;
}

/** Yubiq Code disponibili per il cliente (dai Contracts). */
export async function fetchYubiqCodes(customerId) {
  if (!customerId) return [];
  const data = await request(`${API_ENDPOINTS.CONTRACTS}?customer_id=${customerId}`).catch(() => ({ contracts: [] }));
  return [...new Set((data.contracts || []).map((c) => c.yubiq_code).filter((code) => code != null && code !== ''))];
}

// ---- Form ----
export const EMPTY_PROJECT_FORM = {
  name: '',
  project_code: '',
  customer_id: '',
  tipologia_servizio: '',
  yubiq_code: '',
  active: true,
  ticket_id_prefix: '',
  regex: '',
  regex_desc: '',
  start_date: '',
  end_date: '',
  days_before_block: 1,
  days_after_block: 1,
  previous_month_blocked: false,
  description: '',
};

const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

export const projectToForm = (p) => ({
  name: p.name || '',
  project_code: p.project_code || '',
  customer_id: p.customer_id ?? p.customer?.id ?? '',
  tipologia_servizio: p.tipologia_servizio || '',
  yubiq_code: p.yubiq_code || '',
  active: p.active !== false,
  ticket_id_prefix: p.ticket_id_prefix || '',
  regex: p.regex || '',
  regex_desc: p.regex_desc || '',
  start_date: toDateInput(p.start_date),
  end_date: toDateInput(p.end_date),
  days_before_block: p.days_before_block || 1,
  days_after_block: p.days_after_block || 1,
  previous_month_blocked: !!p.previous_month_blocked,
  description: p.description || '',
});

/**
 * Errori per campo, stesse regole della pagina classica. Con fields si controllano solo quei campi
 * (modifica di una sezione: un progetto vecchio senza data di fine si può comunque modificare altrove).
 */
export function validateProjectForm(form, fields) {
  const e = {};
  if (!form.name.trim()) e.name = 'Required';
  if (!form.project_code.trim()) e.project_code = 'Required';
  if (!form.customer_id) e.customer_id = 'Required';
  if (!form.start_date) e.start_date = 'Required';
  if (!form.end_date) e.end_date = 'Required';
  else if (form.start_date && form.end_date < form.start_date) e.end_date = 'Must be on or after the start date';
  ['days_before_block', 'days_after_block'].forEach((k) => {
    const n = Number(form[k]);
    if (!Number.isInteger(n) || n < 1) e[k] = 'At least 1 day';
  });
  return fields ? Object.fromEntries(Object.entries(e).filter(([k]) => fields.includes(k))) : e;
}

const formToPayload = (form, userId) => ({
  name: form.name.trim(),
  description: form.description.trim() || null,
  customer_id: parseInt(form.customer_id, 10),
  start_date: form.start_date || null,
  end_date: form.end_date || null,
  active: !!form.active,
  project_code: form.project_code.trim(),
  days_after_block: parseInt(form.days_after_block, 10) || 1,
  days_before_block: parseInt(form.days_before_block, 10) || 1,
  ticket_id_prefix: form.ticket_id_prefix.trim() || null,
  regex: form.regex.trim() || null,
  regex_desc: form.regex_desc.trim() || null,
  previous_month_blocked: !!form.previous_month_blocked,
  yubiq_code: form.yubiq_code || null,
  tipologia_servizio: form.tipologia_servizio || null,
  ...(userId ? { user_id: userId } : {}),
});

/** Crea il progetto e, come nella pagina classica, aggiunge chi lo crea come Service Manager. Restituisce l'id. */
export async function createProject(form, userId) {
  const res = await request(API_ENDPOINTS.PROJECTS, jsonOptions('POST', formToPayload(form, userId)));
  invalidateProjectsList();
  const id = res.id ?? res.project?.id;
  if (id && userId) {
    await request(`${API_ENDPOINTS.PROJECT_USERS}?project_id=${id}`, jsonOptions('POST', { user_id: userId, project_role_id: 1 })).catch(() => null);
  }
  return id;
}

export async function updateProject(id, form, userId) {
  await request(`${API_ENDPOINTS.PROJECTS}?id=${id}`, jsonOptions('PUT', formToPayload(form, userId)));
  invalidateProjectsList();
}

export async function deleteProject(id) {
  await request(`${API_ENDPOINTS.PROJECTS}?id=${id}`, { method: 'DELETE' });
  invalidateProjectsList();
}

/**
 * Cosa impedisce di eliminare il progetto (stesse regole del backend): worklog, utenti, team, activities
 * e (se attivo) report mapping. Controlla solo questo progetto. Gli utenti rimossi (validità scaduta)
 * non sono visibili qui ma il backend li conta ancora: in quel caso resta il messaggio del backend.
 */
export async function fetchDeleteBlockers(id) {
  const [w, u, t, a, m] = await Promise.all([
    request(`${API_ENDPOINTS.WORKLOGS}?project_ids=${id}&limit=1&skip=0`),
    request(`${API_ENDPOINTS.PROJECT_USERS}?project_id=${id}`),
    request(`${API_ENDPOINTS.TEAMS}?project_id=${id}`),
    request(`${API_ENDPOINTS.PROJECT_ACTIVITIES}?project_id=${id}`),
    ENABLE_TAB_MAPPING ? request(`${API_ENDPOINTS.SHEET_MAPPING}?project_ids=${id}`) : null,
  ]);
  return {
    worklogs: w.total ?? (w.worklogs || []).length,
    users: (u.project_users || []).length,
    teams: (t.teams || []).length,
    activities: (a.activity_ids || []).length,
    mappings: (m?.project_ids || []).map(String).includes(String(id)),
  };
}

// ---- Activities ----
export async function fetchProjectActivities(id) {
  const [all, selected] = await Promise.all([
    request(`${API_ENDPOINTS.ACTIVITIES}?for_project_config=true`),
    request(`${API_ENDPOINTS.PROJECT_ACTIVITIES}?project_id=${id}`),
  ]);
  return { activities: all.activities || [], selectedIds: selected.activity_ids || [] };
}

export const saveProjectActivities = (id, activityIds) =>
  request(API_ENDPOINTS.PROJECT_ACTIVITIES, jsonOptions('POST', { project_id: parseInt(id, 10), activity_ids: activityIds }));

// ---- Revenues ----
/** Dati economici del progetto (null se non ancora inseriti). */
export async function fetchRevenues(projectId) {
  const data = await request(`${API_ENDPOINTS.REVENUES}?project_id=${projectId}`);
  return data.contract || null;
}

/** Salva (crea o aggiorna) i dati economici; i campi vuoti diventano null. */
export function saveRevenues(projectId, form, userId) {
  const num = (v) => (v === '' || v == null ? null : parseFloat(v));
  return request(
    API_ENDPOINTS.REVENUES,
    jsonOptions('POST', {
      project_id: parseInt(projectId, 10),
      ricavi_actual: num(form.ricavi_actual),
      ricavi_anno_prec: num(form.ricavi_anno_prec),
      ricavi_anno_corrente: num(form.ricavi_anno_corrente),
      ricavi_anno_succ: num(form.ricavi_anno_succ),
      canone_mensile: num(form.canone_mensile),
      ...(userId ? { user_id: userId } : {}),
    }),
  );
}

/** Dati economici di tutti i progetti dell'utente (export "All my projects"). */
export async function fetchMyRevenues(userId) {
  const data = await request(`${API_ENDPOINTS.REVENUES}?user_id=${userId}`);
  return data.contracts || [];
}

// ---- Formattazione ----
/** Fine "aperta": senza data o con anno 9999 (convenzione usata per i progetti senza scadenza). */
export const isOpenEnded = (date) => !date || Number(String(date).slice(0, 4)) >= 9999;

export const fmtDay = (v) => (v ? parseLocalDate(String(v).slice(0, 10)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export const fmtPeriod = (p) => `${fmtDay(p.start_date)} – ${isOpenEnded(p.end_date) ? 'no end date' : fmtDay(p.end_date)}`;
